import { boot } from 'quasar/wrappers'
import { log } from 'src/services/app/log.service'
import { ERROR_TYPES } from 'src/services/consts/api.const'
import { useConfirm } from 'src/composables/app/system/useConfirm'
import { isLocalHost } from 'src/config'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Unable to preload CSS for',
]

let isVersionModalOpen = false

export default boot(() => {
  if (isLocalHost) return log.info('[Version] Version control disabled in local host')

  if (!window._versionControlInitialized) {
    window._versionControlInitialized = true
    setupFetchInterceptor()
    setupChunkErrorHandler()
  }
})

function isChunkOrAssetLoadError(error) {
  if (error == null) return false
  const strings = [
    String(error),
    error?.message,
    error?.cause != null ? String(error.cause) : '',
  ].filter(Boolean)
  return strings.some((str) => CHUNK_ERROR_PATTERNS.some((pattern) => str.includes(pattern)))
}

/**
 * Call from router.onError so every failed navigation (including retries) is handled.
 * Must be registered where the router instance is guaranteed (e.g. router/index.js).
 */
export function handleNavigationError(error) {
  if (isChunkOrAssetLoadError(error)) {
    log.info('[Version] Detected chunk loading error (router):', String(error))
    handlePotentialVersionMismatch()
  }
}

/**
 * Catch script/link load failures (e.g. chunk 404 on initial load). Route chunk failures are handled by router.onError.
 */
function setupChunkErrorHandler() {
  window.addEventListener(
    'error',
    (event) => {
      if (
        event.target &&
        (event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK') &&
        (event.target.src?.includes('/assets/') || event.target.href?.includes('/assets/'))
      ) {
        log.info('[Version] Detected asset loading error:', event.target.src || event.target.href)
        handlePotentialVersionMismatch()
        event.preventDefault()
      }
    },
    true
  )
}

/**
 * Setup fetch interceptor to detect asset 404s / fetch failures under /assets/.
 */
function setupFetchInterceptor() {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args)
      const url = args[0]?.url || args[0]
      if (typeof url === 'string' && url.includes('/assets/')) {
        if (response.status === 404) {
          handlePotentialVersionMismatch()
        }
      }
      return response
    } catch (error) {
      const url = args[0]?.url || args[0]
      if (typeof url === 'string' && url.includes('/assets/')) {
        handlePotentialVersionMismatch()
        // Offline asset failures are a connectivity issue, not a new deploy - don't mislabel.
        error.name = !navigator.onLine ? ERROR_TYPES.OFFLINE : ERROR_TYPES.VERSION_MISMATCH
      }
      throw error
    }
  }
}

/**
 * Handle a potential version mismatch: show modal with "Refresh now" or "Not now". Page reloads only when the user clicks "Refresh now".
 */
function handlePotentialVersionMismatch() {
  if (!navigator.onLine) {
    log.info('[Version] Skipping version modal - offline (connectivity issue, not new version)')
    return
  }
  if (isVersionModalOpen) {
    log.info('[Version] Version modal already open, skipping duplicate')
    return
  }
  isVersionModalOpen = true
  log.info('[Version] Handling version mismatch')
  const { showConfirm } = useConfirm()
  showConfirm({
    title: 'Update available',
    message: 'To keep everything running smoothly, refresh the page to load the newest version.',
    icon: 'refresh-cw',
    confirmText: 'Refresh now',
    cancelText: 'Later',
    onConfirm: () => {
      window.location.reload()
    },
    onCancel: () => {
      isVersionModalOpen = false
    },
  })
}
